import { QuizAnswerConfig, QuizBankConfig, QuizConfigData, QuizQuestionConfig, QuizResult, QuizResults, QuizTreeConfig } from "../Shared/Party/PartyConfigData";

export interface Generator {
    getAnswers(state: string): Array<QuizAnswerConfig>;
    submitAnswer(state: string, props: QuizAnswerConfig): string;
}



export type QuizAction = 'complete' | 'load-new-question' | 'reload-answer-grid' | 'none';

export type Quiz = {
    QuestionTimeOut: () => QuizAction;
    Results: () => QuizResults;
    AnswerGiven: (selectedAnswers: number[], selectedValues: number[], disableTimeLock: boolean) => QuizAction;
    CurrentQuestion: () => QuizQuestionConfig;
    CurrentAnswers: () => QuizAnswerConfig[];
    Points: () => number;
    Time: () => number;
    TimeBonus: () => number;
    TotalPoints: () => number;
    CurrentTimeLimit: () => number;
    QuestionNumber: () => number;
}

type _Quiz = {
    availableAnswers: QuizAnswerConfig[];
    lastAnswerSubmissionTime: number;
    metrics: MetricDict;
    questionNumber: number;
    questionOrders: number[][];
    bankOrder: number[];
    bankIndex: number;
    quizTree: QuizTreeConfig | undefined;
    currentQuestion: QuizQuestionConfig;
    answerBank: QuizAnswerConfig[];
    answerRefillCount: number;
    currentAnswers: QuizAnswerConfig[];
    results: QuizResults;
    currentGenerator: Generator | undefined;
    generatorCallCount: number;
    currentState: string;
    currentTime: number;
    started: number;


}

interface MetricDict {
    [index: string]: number;
}

const convertToDict = (keys: string[]) => {
    const ret: MetricDict = {};
    keys.forEach(element => { ret[element] = 0; });
    return ret;
}

const createIndexArray = (size: number, randomize: boolean, limit: number): number[] => {
    const ordered = new Array<number>();
    for (var i = 0; i < size; i++)
        ordered.push(i);

    if (!randomize) {
        if (limit > 0)
            ordered.splice(limit);
        return ordered;
    }

    const randomized = new Array<number>();
    while (ordered.length > 0) {
        const index = Math.floor(Math.random() * ordered.length);
        randomized.push(ordered[index]);
        ordered.splice(index, 1);
    }
    if (limit > 0)
        randomized.splice(limit);
    return randomized;

}

const generateAnswerForQuestion = (quiz: Quiz, _correct?: number): { selected: number[], values: number[] } => {
    const question = quiz.CurrentQuestion();
    const answers = quiz.CurrentAnswers();
    const answerType = question.answerType ?? 'select';
    const correct = _correct ?? 0.5;

    if (answerType === 'select') {
        const correctAnswers = Array<number>();
        const wrongAnswers = Array<number>();
        answers.forEach((answer, index) => {
            if (answer.correct)
                correctAnswers.push(index);
            else
                wrongAnswers.push(index);
        });

        var doCorrect = Math.random() < correct;
        doCorrect = correctAnswers.length === 0 ? false : (wrongAnswers.length === 0 ? true : doCorrect);
        const index = doCorrect ? correctAnswers[Math.floor(Math.random() * correctAnswers.length)] : wrongAnswers[Math.floor(Math.random() * wrongAnswers.length)];
        return { selected: [index], values: [] };
    }
    else
        return { selected: [], values: [] };
}

export const AutomatedQuizTest = (quiz: Quiz, _correct?: number) => {
    var quizAction: QuizAction = 'none';

    while (quizAction !== 'complete') {
        console.log('Answering Question : ' + quiz.CurrentQuestion().itemId);
        const answer = generateAnswerForQuestion(quiz, _correct);
        //console.log('Generated Answer : ' + JSON.stringify(answer));
        quizAction = quiz.AnswerGiven(answer.selected, answer.values, true);
        console.log('Result Action : ' + quizAction + ' points : ' + quiz.Points() + ' timeBonus : ' + quiz.TimeBonus());
    }
    console.log('Quiz Finished');

}



export const CreateQuiz = (config: QuizConfigData, results: QuizResults): Quiz => {

    const _quiz: _Quiz = {
        availableAnswers: [],
        lastAnswerSubmissionTime: Date.now(),
        metrics: {},
        questionNumber: 0,
        questionOrders: [],
        bankOrder: [],
        bankIndex: 0,
        quizTree: undefined,
        currentQuestion: config.banks[0].simpleQuestions.length > 0 ? config.banks[0].simpleQuestions[0] : config.banks[0].adaptiveQuestions[0].question,
        answerBank: [],
        answerRefillCount: 0,
        currentAnswers: [],
        results: results,
        currentGenerator: undefined,
        generatorCallCount: 0,
        currentState: '',
        currentTime: 0,
        started: Date.now(),


    }


    config.banks.forEach((props: QuizBankConfig) => {
        const randomize = props.scrambleQuestions === undefined ? config.scrambleQuestions : props.scrambleQuestions;
        const size = props.simpleQuestions.length === 0 ? props.adaptiveQuestions.length : props.simpleQuestions.length;

        _quiz.questionOrders.push(createIndexArray(size, randomize, props.pickQuestions ?? 0));
    });
    _quiz.bankOrder = config.bankUsage === "interleaved-random" ? createIndexArray(config.banks.length, true, 0) : createIndexArray(config.banks.length, false, 0);
    PrepareQuestion(_quiz, config);

    const submitAnswer = (selectedAnswers: number[], answerValues: number[]): void => {

        let question: QuizQuestionConfig = _quiz.currentQuestion;
        const points = (question.points !== undefined ? question.points : (config.banks[_quiz.bankOrder[_quiz.bankIndex]].pointsPerQuestion !== undefined ? config.banks[_quiz.bankOrder[_quiz.bankIndex]].pointsPerQuestion : config.pointsPerQuestion)) ?? 0;
        const now = Date.now();
        const result: QuizResult = {
            questionId: question.itemId,
            answers: _quiz.currentAnswers,//.flatMap((value : AnswerProps, _index : number, _array : AnswerProps[])=> { return value.id}),
            selected: selectedAnswers,
            correct: false,
            points: 0,
            values: new Array<number>(),
            metrics: new Array<{ name: string, value: number }>(),
            timeTaken: now - _quiz.lastAnswerSubmissionTime
        }
        _quiz.currentTime = now - _quiz.started;
        _quiz.lastAnswerSubmissionTime = now;

        if (question.answerType === "matchAnswer") {
            result.correct = _quiz.currentAnswers[selectedAnswers[0]].matchId === _quiz.currentAnswers[selectedAnswers[1]].matchId;


        }
        else if (question.answerType === "slide" || question.answerType === "counterSlide") {
            result.values = answerValues;

        }
        else {
            // console.log('selectAnswers : ' + JSON.stringify(selectedAnswers));
            let currentAnswer = _quiz.currentAnswers[selectedAnswers[0]];
            //console.log('currentAnswer : ' + JSON.stringify(currentAnswer));
            result.correct = currentAnswer.correct ?? false;
            if (_quiz.currentGenerator !== undefined) {
                _quiz.currentState = _quiz.currentGenerator.submitAnswer(_quiz.currentState, currentAnswer);
            }
        }

        if (result.correct) {
            _quiz.results.answerPoints += points;
            if (config.timerMode === 'question') {
                const timerResidual = Math.floor((_quiz.lastAnswerSubmissionTime + (config.questionTimer ?? 0) - Date.now() + 1000) / 1000);
                _quiz.results.timeBonus += timerResidual * (config.timeBonusMultiplier ?? 0);
            }
            result.points = points;
            //console.log('Answer correct points : ' + points + ' timeBonus : ' + _quiz.results.timeBonus);
            if (question.metricName !== undefined) {
                _quiz.metrics[question.metricName] += question.metricPoints ?? 0;
                result.metrics.push({ name: question.metricName, value: question.metricPoints ?? 0 });
            }

        }
        const isResults = _quiz.results.results[question.itemId];
        if (isResults === undefined) {
            const arr = new Array<QuizResult>();
            arr.push(result);
            _quiz.results.results[question.itemId] = arr;
        }
        else
            isResults.push(result);


    }

    const navigateQuizTree = (selectedAnswers: number[], answerValues: number[]): boolean => {
        if (_quiz.quizTree !== undefined) {
            if (_quiz.quizTree.children === undefined)
                _quiz.quizTree = undefined;
            else if (_quiz.quizTree.children.length < 2) {
                _quiz.quizTree = undefined;
            }
            else {
                switch (_quiz.quizTree.algo) {
                    case "map":
                        {
                            if (_quiz.quizTree.children === undefined)
                                _quiz.quizTree = undefined;
                            else {
                                const selected = (() => {
                                    const s = selectedAnswers.length === 0 ? 0 : selectedAnswers[0];
                                    const r = s > (_quiz.quizTree.children === undefined ? 0 : _quiz.quizTree.children.length) ? 0 : s;
                                    return r;
                                })();
                                _quiz.quizTree = _quiz.quizTree.children[selected];
                            }
                        }
                        break;
                    case "threshA":
                        {
                            const test = _quiz.metrics[_quiz.quizTree.metricA ?? 'default'] > (_quiz.quizTree.threshA ?? 0);
                            _quiz.quizTree = _quiz.quizTree.children !== undefined ? (test ? _quiz.quizTree.children[1] : _quiz.quizTree.children[0]) : undefined;
                        }
                        break;
                    case "threshAandB":
                        {
                            const testA = _quiz.metrics[_quiz.quizTree.metricA ?? 'default'] > (_quiz.quizTree.threshA ?? 0);
                            const testB = _quiz.metrics[_quiz.quizTree.metricB ?? "default"] > (_quiz.quizTree.threshB ?? 0);
                            const test = testA && testB;
                            _quiz.quizTree = _quiz.quizTree.children !== undefined ? (test ? _quiz.quizTree.children[1] : _quiz.quizTree.children[0]) : undefined;
                        }
                        break;
                    case "threshAorB":
                        {
                            const testA = _quiz.metrics[_quiz.quizTree.metricA ?? 'default'] > (_quiz.quizTree.threshA ?? 0);
                            const testB = _quiz.metrics[_quiz.quizTree.metricB ?? "default"] > (_quiz.quizTree.threshB ?? 0);
                            const test = testA || testB;
                            _quiz.quizTree = _quiz.quizTree.children !== undefined ? (test ? _quiz.quizTree.children[1] : _quiz.quizTree.children[0]) : undefined;
                        }
                        break;
                    case "threshAnotB":
                        {
                            const testA = _quiz.metrics[_quiz.quizTree.metricA ?? 'default'] > (_quiz.quizTree.threshA ?? 0);
                            const testB = _quiz.metrics[_quiz.quizTree.metricB ?? "default"] > (_quiz.quizTree.threshB ?? 0);
                            const test = testA && (!testB);
                            _quiz.quizTree = _quiz.quizTree.children !== undefined ? (test ? _quiz.quizTree.children[1] : _quiz.quizTree.children[0]) : undefined;
                        }
                        break;
                    case "threshAxorB":
                        {
                            const testA = _quiz.metrics[_quiz.quizTree.metricA ?? 'default'] > (_quiz.quizTree.threshA ?? 0);
                            const testB = _quiz.metrics[_quiz.quizTree.metricB ?? "default"] > (_quiz.quizTree.threshB ?? 0);
                            const test = (testA || testB) && (!(testA && testB));
                            _quiz.quizTree = _quiz.quizTree.children !== undefined ? (test ? _quiz.quizTree.children[1] : _quiz.quizTree.children[0]) : undefined;
                        }
                        break;
                    case "AgtB":
                        {
                            const test = _quiz.metrics[_quiz.quizTree.metricA ?? 'default'] > _quiz.metrics[_quiz.quizTree.metricB ?? "default"];
                            _quiz.quizTree = _quiz.quizTree.children !== undefined ? (test ? _quiz.quizTree.children[1] : _quiz.quizTree.children[0]) : undefined;
                        }
                        break;
                    default:
                        {
                            const testA = _quiz.metrics[_quiz.quizTree.metricA ?? 'default'] > (_quiz.quizTree.threshA ?? 0);
                            const testB = _quiz.metrics[_quiz.quizTree.metricB ?? "default"] > (_quiz.quizTree.threshB ?? 0);
                            const ci = testA ? (testB ? 3 : 1) : (testB ? 2 : 0);
                            _quiz.quizTree = _quiz.quizTree.children !== undefined ? _quiz.quizTree.children[ci] : undefined;
                        }
                }
            }

            if (_quiz.quizTree !== undefined) {
                _quiz.currentQuestion = _quiz.quizTree.question;
                PrepareAnswers(_quiz, config);
                return true;
            }
            else return false;
        }
        else
            return false;

    }

    const progressToNextQuestion = (): boolean => {

        _quiz.bankIndex++;
        const startBank = _quiz.bankIndex;
        _quiz.bankIndex = _quiz.bankIndex % _quiz.bankOrder.length;

        while ((_quiz.questionOrders[_quiz.bankOrder[_quiz.bankIndex]].length === 0) && (_quiz.bankIndex !== startBank)) {
            _quiz.bankIndex++;
            _quiz.bankIndex = _quiz.bankIndex % _quiz.bankOrder.length;
        }
        if (_quiz.questionOrders[_quiz.bankOrder[_quiz.bankIndex]].length === 0)
            return false;
        else
            return true;
    }

    const wrapUpQuiz = () => {
        if(config.timerMode === 'total') {
            const timerResidual = Math.floor(((config.questionTimer ?? 60000) - _quiz.currentTime) / 1000);
                _quiz.results.timeBonus += timerResidual * (config.timeBonusMultiplier ?? 0);

        }
    }

    const moveToNextQuestion = (selectedAnswers: number[], answerValues: number[]): QuizAction => {
        const nextQuizTree = navigateQuizTree(selectedAnswers, answerValues);

        if (!nextQuizTree) {

            if (config.banks.length === 1) {
                const end = _quiz.questionOrders[0].length === 0;
                if (!end) {
                    PrepareQuestion(_quiz, config);
                    return 'load-new-question';
                }
                else{
                    wrapUpQuiz();
                    return 'complete';
                }
            }
            else if (config.bankUsage === "sequential") {
                if (_quiz.questionOrders[_quiz.bankIndex].length === 0) {
                    _quiz.bankIndex++;
                    const end = _quiz.bankIndex >= _quiz.bankOrder.length;

                    if (!end) {
                        PrepareQuestion(_quiz, config);
                        return 'load-new-question';
                    }
                    else{
                        wrapUpQuiz();
                        return 'complete';
                    }

                }
                else {
                    PrepareQuestion(_quiz, config);
                    return 'load-new-question';
                }

            }
            else {
                const isNext = progressToNextQuestion();
                if (isNext) {
                    PrepareQuestion(_quiz, config);
                    return 'load-new-question';
                }
                else{
                    wrapUpQuiz();
                    return 'complete';
                }


            }
        }
        else
            return 'load-new-question';

    }

    const ret: Quiz = {
        QuestionTimeOut: (): QuizAction => {
            return moveToNextQuestion([], []);
        },

        AnswerGiven: (selectedAnswers: number[], answerValues: number[], disableTimeLock: boolean): QuizAction => {
            const sinceTime = Date.now() - _quiz.lastAnswerSubmissionTime;
            if (sinceTime < 1000 && (!disableTimeLock))
                return 'none';
            submitAnswer(selectedAnswers, answerValues);
            var isRefill = false;

            if (_quiz.currentGenerator !== undefined) {

                if (((_quiz.currentQuestion.generateRounds ?? 0) > 0) && (_quiz.generatorCallCount >= (_quiz.currentQuestion.generateRounds ?? 0))) {
                    _quiz.currentGenerator = undefined;
                    _quiz.currentState = "";
                    _quiz.generatorCallCount = 0;
                }
                else {
                    const newAnswers = _quiz.currentGenerator.getAnswers(_quiz.currentState);
                    if (newAnswers.length === 0) {
                        _quiz.currentGenerator = undefined;
                        _quiz.currentState = "";
                        _quiz.generatorCallCount = 0;
                    }
                    else {
                        _quiz.currentAnswers = newAnswers;
                        _quiz.generatorCallCount++;
                        return 'reload-answer-grid';
                    }
                }
            }
            else {
                isRefill = _quiz.currentQuestion.refill ?? false;
                if (isRefill) {

                    if ((_quiz.currentQuestion.refillCount ?? 0) > 0) {
                        _quiz.answerRefillCount++;
                        if (_quiz.answerRefillCount >= (_quiz.currentQuestion.refillCount ?? 0)) {
                            isRefill = false;
                        }
                        else {
                            _quiz.currentAnswers = getAnswersFromQuestion(_quiz.currentQuestion, _quiz.answerBank, true);
                            //render call here
                            isRefill = true;
                        }

                    }
                    else {
                        const newAnswers = getAnswersFromQuestion(_quiz.currentQuestion, _quiz.answerBank, true);
                        if (newAnswers.length === (_quiz.currentQuestion.pickAnswers ?? 0)) {
                            _quiz.currentAnswers = newAnswers;
                            isRefill = true;
                        }
                        else
                            isRefill = false;
                    }
                }
            }


            if (!isRefill)
                return moveToNextQuestion(selectedAnswers, answerValues);
            else
                return 'reload-answer-grid';
        },

        CurrentQuestion: () => _quiz.currentQuestion,
        CurrentAnswers: () => _quiz.currentAnswers,
        Points: () => _quiz.results.answerPoints,
        Time: () => _quiz.currentTime,
        TimeBonus: () => _quiz.results.timeBonus,
        TotalPoints: () => _quiz.results.answerPoints + _quiz.results.timeBonus,
        CurrentTimeLimit: () => {
            if (config.timerMode === 'none')
                return 1000000;
            else if (config.timerMode === 'total')
                return Math.round((config.questionTimer ?? 60000) / 1000);
            else
                return Math.round((_quiz.currentQuestion.questionTimer ?? (config.banks[_quiz.bankOrder[_quiz.bankIndex]].questionTimer ?? (config.questionTimer ?? 15000))) / 1000) + 1
        },
        QuestionNumber: () => _quiz.questionNumber,
        Results: () => _quiz.results

    };

    return ret;


}


const PrepareQuestion = (quiz: _Quiz, config: QuizConfigData) => {
    //  console.log('PrepareQuestion called @ ' + Date.now() + ' currentQ : ' + quiz.currentQuestion.itemId);

    const adaptive = config.banks[quiz.bankOrder[quiz.bankIndex]].adaptiveQuestions.length > 0;
    if (adaptive) {
        quiz.quizTree = config.banks[quiz.bankIndex].adaptiveQuestions[quiz.questionOrders[quiz.bankIndex][0]];
        quiz.currentQuestion = quiz.quizTree.question;

    }
    else
        quiz.currentQuestion = config.banks[quiz.bankIndex].simpleQuestions[quiz.questionOrders[quiz.bankIndex][0]];
    quiz.questionOrders[quiz.bankIndex].splice(0, 1);
    PrepareAnswers(quiz, config);

}

const PrepareAnswers = (quiz: _Quiz, config: QuizConfigData) => {
    const scramble = (quiz.currentQuestion.scramble !== undefined ? quiz.currentQuestion.scramble : config.banks[quiz.bankOrder[quiz.bankIndex]].scrambleAnswers) ?? config.scrambleAnswers;

    quiz.answerBank = quiz.currentQuestion.answers.slice(0);
    quiz.answerRefillCount = 0;
    quiz.questionNumber++;

    quiz.availableAnswers = quiz.currentQuestion.answers.slice(0);
    if (quiz.currentQuestion.addAnswersToImages !== undefined) {
        for (var i = 0; i < quiz.currentQuestion.addAnswersToImages.length; i++) {
            const questionId = quiz.currentQuestion.addAnswersToImages[i];
            const qr = quiz.results.results[questionId];
            const images = quiz.currentQuestion.images !== undefined ? quiz.currentQuestion.images : (() => { quiz.currentQuestion.images = Array<string>(); return quiz.currentQuestion.images })();
            if (qr !== undefined) {
                const image = qr[0].answers[0].image;
                if (image !== undefined)
                    images.push(image);
            }
        }
    }

    if (quiz.currentQuestion.addAnswers !== undefined) {
        for (var i = 0; i < quiz.currentQuestion.addAnswers.length; i++) {
            const questionId = quiz.currentQuestion.addAnswers[i];
            const qr = quiz.results.results[questionId];
            if (qr !== undefined) {
                for (var j = 0; j < qr.length; j++) {
                    quiz.availableAnswers.push(...qr[j].answers);
                }
            }
        }
    }

    quiz.currentAnswers = getAnswersFromQuestion(quiz.currentQuestion, quiz.availableAnswers, scramble);
}


const getAnswersFromQuestion = (question: QuizQuestionConfig, availableAnswers: Array<QuizAnswerConfig>, scramble: boolean): Array<QuizAnswerConfig> => {
    const pickAnswers = question.pickAnswers ?? 0;

    if ((pickAnswers > 0) || scramble) {
        const ret = new Array<QuizAnswerConfig>();
        const pick = pickAnswers > 0 ? pickAnswers : question.answers.length;

        for (var i = 0; (i < pick && availableAnswers.length > 0); i++) {
            const index = Math.floor(Math.random() * availableAnswers.length);
            ret.push(availableAnswers[index]);
            availableAnswers.splice(index, 1);
        }
        return ret;
    }
    else
        return question.answers;
}